import {Injectable} from '@angular/core';
import {Router} from '@angular/router';

export interface IframeRedirectPayload {
    path?: string;
    url?: string;
    replaceUrl?: boolean;
    target?: 'internal' | 'external';
}

export interface IframeRedirectMessage {
    type: 'APP_REDIRECT';
    payload: IframeRedirectPayload;
}

@Injectable({providedIn: 'root'})
export class IframeRedirectService {
    private static listenerRegistered = false;
    private readonly messageHandler = (event: MessageEvent): void => this.handleMessage(event);
    private allowedOrigins: string[] = [window.location.origin];
    private allowedRoutes: RegExp[] = [
        /^\/$/,
        /^\/login$/,
        /^\/register$/,
        /^\/forgot-password$/,
        /^\/dashboard$/,
        /^\/profil$/,
        /^\/lessons(?:\/.*)?$/,
        /^\/cours\/.+$/,
        /^\/activites\/.+$/,
        /^\/ressources\/.+$/,
        /^\/apprendre\/.+$/
    ];

    constructor(private router: Router) {
    }

    public configure(options: {
        allowedOrigins?: string[];
        allowedRoutes?: RegExp[];
    }): void {
        if (options.allowedOrigins?.length) {
            this.allowedOrigins = Array.from(new Set([
                ...options.allowedOrigins.map(origin => origin.trim()).filter(Boolean),
                window.location.origin
            ]));
        }

        if (options.allowedRoutes?.length) {
            this.allowedRoutes = options.allowedRoutes;
        }
    }

    public listen(): void {
        if (IframeRedirectService.listenerRegistered) {
            return;
        }

        window.addEventListener('message', this.messageHandler, false);
        IframeRedirectService.listenerRegistered = true;
    }

    public removeListener(): void {
        window.removeEventListener('message', this.messageHandler, false);
        IframeRedirectService.listenerRegistered = false;
    }

    private handleMessage(event: MessageEvent): void {
        if (!this.isAllowedOrigin(event.origin)) {
            return;
        }

        const data = event.data;
        if (!data || typeof data !== 'object' || data.type !== 'APP_REDIRECT' || !data.payload) {
            return;
        }

        const safeUrl = this.buildSafeUrl(data.payload.path ?? data.payload.url, data.payload.target);
        if (!safeUrl) {
            return;
        }

        if (this.shouldUseExternalNavigation(safeUrl, data.payload.target)) {
            window.location.assign(safeUrl.href);
            return;
        }

        const replaceUrl = data.payload.replaceUrl ?? true;
        const internalPath = `${safeUrl.pathname}${safeUrl.search}${safeUrl.hash}` || '/';
        this.router.navigateByUrl(internalPath, {replaceUrl});
    }

    private isAllowedOrigin(origin: string): boolean {
        return this.allowedOrigins.includes(origin);
    }

    private shouldUseExternalNavigation(rawUrl: URL, target?: 'internal' | 'external'): boolean {
        if (target === 'external') {
            return true;
        }

        return rawUrl.origin !== window.location.origin;
    }

    private buildSafeUrl(rawPath?: string, target?: 'internal' | 'external'): URL | null {
        if (!rawPath || typeof rawPath !== 'string') {
            return null;
        }

        try {
            const url = new URL(rawPath, window.location.origin);
            const isSameOrigin = url.origin === window.location.origin;

            if (target === 'internal' && !isSameOrigin) {
                return null;
            }

            if (target === 'external') {
                if (!this.allowedOrigins.includes(url.origin)) {
                    return null;
                }
                return url;
            }

            if (isSameOrigin) {
                const isAllowedRoute = this.allowedRoutes.some((pattern) => pattern.test(url.pathname));
                if (!isAllowedRoute) {
                    return null;
                }
                return url;
            }

            if (!this.allowedOrigins.includes(url.origin)) {
                return null;
            }

            return url;
        } catch {
            return null;
        }
    }
}
